/*
 * Copyright 2021 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ReactNode, FC } from 'react';

import { PageWithHeader } from '@backstage/core-components';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import { techdocsTranslationRef } from '../../translation';

/**
 * Props for {@link TechDocsPageWrapper}
 *
 * @public
 */
export type TechDocsPageWrapperProps = {
  children?: ReactNode;
  CustomPageWrapper?: FC<{ children?: ReactNode }>;
};

/**
 * Component wrapping a TechDocs page with Page and Header components
 *
 * @public
 */
export const TechDocsPageWrapper = (props: TechDocsPageWrapperProps) => {
  const { children, CustomPageWrapper } = props;
  const configApi = useApi(configApiRef);
  const { t } = useTranslationRef(techdocsTranslationRef);
  const orgName =
    configApi.getOptionalString('organization.name') ?? 'Backstage';
  const generatedSubtitle = t('pageWrapper.subtitle', { orgName });

  return (
    <>
      {CustomPageWrapper ? (
        <CustomPageWrapper>{children}</CustomPageWrapper>
      ) : (
        <PageWithHeader
          title={t('pageWrapper.title')}
          subtitle={generatedSubtitle}
          themeId="documentation"
        >
          {children}
        </PageWithHeader>
      )}
    </>
  );
};
